// 8 方位のリサイズハンドル用ドラッグハンドラ。
//
// 振る舞い:
// - pointerdown でドラッグ開始（基準位置を捕捉）
// - pointermove でステージ座標差分を方位に応じて x/y/w/h に反映し、対象 DOM を
//   即時プレビュー（doc store/サーバへは送らない）
// - pointerup で最終 x/y/w/h を transformObject op として送信し永続化
// - pointercancel（ブラウザ / OS による中断、2 本指ピンチの開始など）では確定せず、
//   直書きしたプレビューをストア値へ巻き戻して破棄する
//
// 回転対応:
// - object.rotation を受け取り、ポインタ移動量を逆回転 (-θ) して
//   object-local 軸基準のリサイズへ変換する。これにより、回転後の図形でも
//   east ハンドルなら横方向のみが伸び縦方向は変化しない（縦横が連動するバグの修正）。
// - リサイズに伴うローカル中心移動量を世界座標へ +θ で戻して新しい AABB の x/y を求める。
//
// 制約:
// - 最小 16px 制限（潰れ防止）
// - Shift キー押下中は、四隅ハンドル（nw / ne / se / sw）のみアスペクト比を初期値で固定する。
//   辺の中央ハンドル（n / s / e / w）は片軸のみのリサイズなので Shift は無視する。
// - lockAspect=true（画像・動画・スタンプなど）の場合は Shift の有無に関わらず四隅で常に
//   アスペクト比を固定する。これらは選択時に辺ハンドルを出さない運用のため、四隅のみ反応する。

import { useEffect, useRef, type RefObject } from "react";
import { clampFrameToCanvas } from "@refinement-canvas/domain";
import { CANVAS_SNAP_PX } from "../../../lib/canvas-coordinates";
import { clampAspectFrameToCanvas } from "../../../lib/aspect-clamp";
import {
  SNAP_THRESHOLD_SCREEN_PX,
  computeResizeAlignment,
  type AlignmentBox,
  type AlignmentGuide,
} from "../../../lib/canvas-alignment";
import { collectAlignmentStatics } from "./collect-alignment-statics";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useAlignmentGuideStore } from "../store/canvas-alignment-store";
import { useConnectorDragStore } from "../store/connector-drag-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

export type ResizeDirection = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

interface Options {
  readonly direction: ResizeDirection;
  readonly handleRef: RefObject<HTMLElement | null>;
  readonly objectId: string;
  readonly dispatcher: CanvasOpDispatcher;
  readonly initialX: number;
  readonly initialY: number;
  readonly initialW: number;
  readonly initialH: number;
  /** 対象オブジェクトの回転角（度）。0 または未指定なら従来通りワールド軸の挙動。 */
  readonly initialRotation?: number | undefined;
  /**
   * アスペクト比固定。true のとき四隅ハンドルは Shift の有無に関わらず常に初期 w/h 比を維持する。
   * 画像・動画・スタンプなど、片軸リサイズに意味の無いオブジェクトで使う。
   */
  readonly lockAspect?: boolean | undefined;
  /**
   * 種別固有の最小幅・最小高さ（ステージ px）。未指定は共通の MIN_SIZE_PX（16）。
   * セクション（200×120）のようにタイトルバー等が潰れない下限を種別ごとに課すために使う。
   */
  readonly minW?: number | undefined;
  readonly minH?: number | undefined;
  /** 対象オブジェクトの DOM 要素（プレビュー用に left/top/width/height を直接書き換える）。 */
  readonly targetRef: RefObject<HTMLElement | null>;
}

const MIN_SIZE_PX = 16;
// 他参加者向けゴースト中継の最小送信間隔（ms）。約 20Hz に間引いて通信量を抑える。
const TRANSIENT_THROTTLE_MS = 50;

export function useResizeHandle(options: Options): void {
  const {
    direction,
    handleRef,
    objectId,
    dispatcher,
    initialX,
    initialY,
    initialW,
    initialH,
    initialRotation,
    lockAspect,
    targetRef,
  } = options;
  // 種別固有の最小サイズ。未指定は共通の下限（16px）で潰れ防止のみ行う。
  const minW = options.minW ?? MIN_SIZE_PX;
  const minH = options.minH ?? MIN_SIZE_PX;
  // 参照専用（埋め込み）ではリサイズを無効化する。
  const readOnly = useCanvasStore((s) => s.readOnly);

  // ドラッグ状態は effect のクロージャではなく ref に保持する（最重要）。
  // dispatcher の identity 変化や同一オブジェクトへのリモート更新で「ドラッグ中に」この effect が
  // 再バインドされると、クロージャ保持ではドラッグ状態（特に pointerup での確定処理）ごと失われ、
  // 直接 DOM プレビューだけが残って確定 op が送られない＝「枠線とつまみがサイズ変更へ追従しない」
  // 実障害になる。ref なら再バインド後のリスナーが同じ進行中ドラッグを引き継げる。
  // 計算基準（開始時のフレーム・回転）もドラッグ状態に固定し、再バインドで props の initial* が
  // 変わっても進行中ドラッグの計算がぶれないようにする。
  const draggingRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    baseX: number;
    baseY: number;
    baseW: number;
    baseH: number;
    baseRotation: number;
    // 最後の pointermove プレビューで確定した枠。pointerup ではこれをそのまま採用し、
    // 「見た目（最後のプレビュー）」と「確定値」を完全一致させる。
    // event 座標と event.shiftKey から再計算すると、リリース直前の Shift 解除や僅かな
    // 座標変化でアスペクト比固定の主従が反転し、確定値だけずれる恐れがあるため枠を保持する。
    lastPreviewFrame: { x: number; y: number; w: number; h: number } | null;
    // 最後の pointermove でアスペクト比固定が成立していたか。grid 吸着の確定時に、
    // サイズをグリッドへ丸めず比率を保つ専用 clamp を使うかの判定に使う（プレビューと一致させる）。
    lastKeepAspect: boolean;
    // 整列スナップ（スマートガイド）の対象矩形。ドラッグ開始時に 1 回だけ収集して固定する。
    staticBoxes: readonly AlignmentBox[];
    // 最後の pointermove で整列スナップが成立した軸。確定時に成立軸のグリッド丸めをスキップする。
    lastMatchedX: boolean;
    lastMatchedY: boolean;
    // 他参加者向けゴースト中継を間引くための直近送信時刻（ms）。
    lastTransientAt: number;
  } | null>(null);

  // アンマウント時の後始末（選択解除・種別切替などで選択オーバーレイごと消える経路）。
  // 下の effect の teardownOrphanDrag は「effect が再実行されて readOnly / handle 無しの経路に
  // 入ったとき」しか走らず、unmount では cleanup（listener 解除）だけが走って進行中ドラッグと
  // コネクタ用ライブ枠が残留し得る。空依存（objectId のみ）effect の cleanup は listener の
  // 再バインドでは走らず真のアンマウント時のみ実行されるため、「effect 再バインドを跨いで
  // 進行中ドラッグを維持する」設計（draggingRef）と両立したまま残留だけを確実に片付けられる
  // （選択オーバーレイは key={id} で張り替えるため objectId はフック寿命内で不変）。
  useEffect(() => {
    return () => {
      if (!draggingRef.current) return;
      draggingRef.current = null;
      // 直書きした left/top/width/height プレビューをストア値へ巻き戻す。オーバーレイが
      // 消えた後は自己修復（canvas-selection-overlay）も走らないため、ここで戻さないと
      // 本体 DOM だけプレビュー位置のまま残留する。
      revertResizePreview(targetRef.current, objectId);
      useConnectorDragStore.getState().clearFrames([objectId]);
      useAlignmentGuideStore.getState().clearGuides();
    };
  }, [objectId, targetRef]);

  useEffect(() => {
    // readOnly 切替やハンドル消滅で listener を張らない経路に入るとき、進行中ドラッグが
    // 残っていれば破棄する（listener が無いままでは pointerup / lostpointercapture を拾えず
    // 確定不能）。コネクタ追従のライブ枠も片付け、選択オーバーレイの自己修復スキップが
    // 当該オブジェクトへ残留しないようにする（use-object-drag の teardown と同じ方針）。
    const teardownOrphanDrag = (): void => {
      if (!draggingRef.current) return;
      draggingRef.current = null;
      // unmount 経路の後始末と同様に、直書きプレビューもストア値へ巻き戻す。
      revertResizePreview(targetRef.current, objectId);
      useConnectorDragStore.getState().clearFrames([objectId]);
      useAlignmentGuideStore.getState().clearGuides();
    };
    if (readOnly) {
      teardownOrphanDrag();
      return undefined;
    }
    const handle = handleRef.current;
    if (!handle) {
      teardownOrphanDrag();
      return undefined;
    }

    // 四隅（斜め）ハンドルか。アスペクト比固定の判定・境界 clamp の分岐で使う（direction は不変）。
    const isCorner =
      direction === "nw" || direction === "ne" || direction === "se" || direction === "sw";
    // この方位・対象でアスペクト比固定が成立するか（shiftKey は move ごとに変わるため引数で受ける。
    // 比の分母になる基準サイズは、進行中ドラッグがあればその開始時の値を使う）。
    const keepsAspectFor = (shiftKey: boolean, baseW: number, baseH: number): boolean =>
      (shiftKey || lockAspect === true) && isCorner && baseW > 0 && baseH > 0;

    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0) return;
      const baseRotation = initialRotation ?? 0;
      // 整列スナップの対象は、非回転リサイズのときだけドラッグ開始時に 1 回収集して固定する
      // （回転リサイズ・アスペクト比固定では整列を行わないため収集不要）。移動ドラッグと同じ
      // collectAlignmentStatics を共有し、自分自身を除外する。
      const staticBoxes =
        baseRotation === 0
          ? collectAlignmentStatics(new Set([objectId]), useCanvasStore.getState().view)
          : [];
      draggingRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        baseX: initialX,
        baseY: initialY,
        baseW: initialW,
        baseH: initialH,
        baseRotation,
        lastPreviewFrame: null,
        lastKeepAspect: keepsAspectFor(event.shiftKey, initialW, initialH),
        staticBoxes,
        lastMatchedX: false,
        lastMatchedY: false,
        lastTransientAt: 0,
      };
      // jsdom などポインタキャプチャ未実装の環境でも落ちないよう存在チェックする。
      handle.setPointerCapture?.(event.pointerId);
      // 親オブジェクトの drag や stage の pan を起こさせない
      event.stopPropagation();
    };

    const computeResized = (
      dragging: NonNullable<typeof draggingRef.current>,
      clientX: number,
      clientY: number,
      shiftKey: boolean
    ): {
      frame: { x: number; y: number; w: number; h: number };
      guides: readonly AlignmentGuide[];
      matchedX: boolean;
      matchedY: boolean;
    } => {
      const view = useCanvasStore.getState().view;
      const worldDx = (clientX - dragging.startClientX) / view.zoom;
      const worldDy = (clientY - dragging.startClientY) / view.zoom;
      // 計算基準はドラッグ開始時に固定した値を使う（effect 再バインドで props が変わっても不変）。
      const { baseX, baseY, baseW, baseH, baseRotation } = dragging;

      // 方位ごとに左/右/上/下のどれを動かすかを決める
      const movesLeft = direction === "nw" || direction === "w" || direction === "sw";
      const movesRight = direction === "ne" || direction === "e" || direction === "se";
      const movesTop = direction === "nw" || direction === "n" || direction === "ne";
      const movesBottom = direction === "sw" || direction === "s" || direction === "se";

      // 世界座標のポインタ移動量をオブジェクト・ローカル軸へ写像する。
      // 回転 θ で描画されている図形に対し、ローカル軸はワールド軸を -θ 回転したもの。
      const rad = (baseRotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      let localDx = worldDx * cos + worldDy * sin;
      let localDy = -worldDx * sin + worldDy * cos;

      // Shift 押下中、四隅ハンドルのみ初期アスペクト比 (w/h) を維持するようローカル差分を補正する。
      // ローカル軸上で対角方向への移動量を、初期 w/h 比に従って w または h どちらが
      // より大きく動こうとしているかで主導軸を決め、もう一方を比率で連動させる。
      // lockAspect 対象は Shift 無しでも常にアスペクト比固定とする（keepsAspectFor に集約）。
      const keepAspect = keepsAspectFor(shiftKey, baseW, baseH);
      if (keepAspect && baseW > 0 && baseH > 0) {
        const aspect = baseW / baseH;
        // 四隅の方位ごとに、w/h を増やす方向を符号として整える。
        const signX = direction === "ne" || direction === "se" ? 1 : -1;
        const signY = direction === "sw" || direction === "se" ? 1 : -1;
        const desiredDw = signX * localDx;
        const desiredDh = signY * localDy;
        // 主導軸の絶対値が大きい方に追随させる（w が動く量に合わせて h を比率で揃える）。
        if (Math.abs(desiredDw) >= Math.abs(desiredDh) * aspect) {
          const adjustedDh = desiredDw / aspect;
          localDy = signY * adjustedDh;
        } else {
          const adjustedDw = desiredDh * aspect;
          localDx = signX * adjustedDw;
        }
      }

      let nextW = baseW;
      let nextH = baseH;
      let centerLocalShiftX = 0;
      let centerLocalShiftY = 0;

      // 横方向: w を変化させ、ローカル X 上で中心が effectiveLocalDx/2 ずれる。
      if (movesLeft) {
        let effectiveLocalDx = localDx;
        let candidateW = baseW - effectiveLocalDx;
        if (candidateW < minW) {
          effectiveLocalDx = baseW - minW;
          candidateW = minW;
        }
        nextW = candidateW;
        centerLocalShiftX = effectiveLocalDx / 2;
      } else if (movesRight) {
        let effectiveLocalDx = localDx;
        let candidateW = baseW + effectiveLocalDx;
        if (candidateW < minW) {
          effectiveLocalDx = minW - baseW;
          candidateW = minW;
        }
        nextW = candidateW;
        centerLocalShiftX = effectiveLocalDx / 2;
      }

      // 縦方向も同様にローカル Y 上で計算する。
      if (movesTop) {
        let effectiveLocalDy = localDy;
        let candidateH = baseH - effectiveLocalDy;
        if (candidateH < minH) {
          effectiveLocalDy = baseH - minH;
          candidateH = minH;
        }
        nextH = candidateH;
        centerLocalShiftY = effectiveLocalDy / 2;
      } else if (movesBottom) {
        let effectiveLocalDy = localDy;
        let candidateH = baseH + effectiveLocalDy;
        if (candidateH < minH) {
          effectiveLocalDy = minH - baseH;
          candidateH = minH;
        }
        nextH = candidateH;
        centerLocalShiftY = effectiveLocalDy / 2;
      }

      // ローカル中心移動量を世界座標へ戻し、AABB の左上座標 (x,y) を更新する。
      const worldCenterShiftX = centerLocalShiftX * cos - centerLocalShiftY * sin;
      const worldCenterShiftY = centerLocalShiftX * sin + centerLocalShiftY * cos;
      const originalCenterX = baseX + baseW / 2;
      const originalCenterY = baseY + baseH / 2;
      const newCenterX = originalCenterX + worldCenterShiftX;
      const newCenterY = originalCenterY + worldCenterShiftY;
      const nextX = newCenterX - nextW / 2;
      const nextY = newCenterY - nextH / 2;

      // リサイズ中もキャンバス境界を越えないよう、最終フレームを必ず有効範囲へ丸める。
      // lockAspect の四隅リサイズでは、境界到達時に w/h を個別丸めすると比率が崩れるため、
      // 同一スケールで縮小してアスペクト比を維持する専用 clamp を使う。
      const rawFrame = { x: nextX, y: nextY, w: nextW, h: nextH };

      // 整列スマートガイド: 非回転かつアスペクト比非固定のときのみ、他図形の端・中心へ辺を揃え、
      // または他図形と同じ幅・高さへサイズを一致させる（後端にもガイドを出す）。回転・比率固定時は
      // 幾何が崩れるため無効化し、従来の clamp のみ行う（移動ドラッグの回転除外と同じ方針）。
      if (!keepAspect && baseRotation === 0 && dragging.staticBoxes.length > 0) {
        const threshold = SNAP_THRESHOLD_SCREEN_PX / view.zoom;
        const aligned = computeResizeAlignment(
          rawFrame,
          { movesLeft, movesRight, movesTop, movesBottom },
          dragging.staticBoxes,
          threshold
        );
        // 整列後も最小サイズを満たす場合のみ採用する（相手が極小でも潰れないようにする）。
        if (aligned.frame.w >= minW && aligned.frame.h >= minH) {
          const clamped = clampFrameToCanvas(aligned.frame);
          // キャンバス境界 clamp で整列意図からずれた場合は、線と実枠がずれるためガイドを出さない
          // （移動経路の clamp 時ガイド非表示ポリシーと同方針）。
          const clampChanged =
            Math.abs(clamped.x - aligned.frame.x) > 1e-6 ||
            Math.abs(clamped.y - aligned.frame.y) > 1e-6 ||
            Math.abs(clamped.w - aligned.frame.w) > 1e-6 ||
            Math.abs(clamped.h - aligned.frame.h) > 1e-6;
          if (!clampChanged) {
            return {
              frame: clamped,
              guides: aligned.guides,
              matchedX: aligned.matchedX,
              matchedY: aligned.matchedY,
            };
          }
          return { frame: clamped, guides: [], matchedX: false, matchedY: false };
        }
      }

      const clampedRaw = keepAspect
        ? clampAspectFrameToCanvas(rawFrame)
        : clampFrameToCanvas(rawFrame);
      return { frame: clampedRaw, guides: [], matchedX: false, matchedY: false };
    };

    const onPointerMove = (event: PointerEvent): void => {
      const dragging = draggingRef.current;
      if (!dragging || dragging.pointerId !== event.pointerId) return;
      // ドラッグ中の Shift 切り替えにも追従できるよう、最新のキー状態で再計算する。
      const resized = computeResized(dragging, event.clientX, event.clientY, event.shiftKey);
      const next = resized.frame;
      // 確定時に同じ枠・同じアスペクト固定判定・整列成立軸を使うため、最後の値を保持する。
      dragging.lastPreviewFrame = next;
      dragging.lastKeepAspect = keepsAspectFor(event.shiftKey, dragging.baseW, dragging.baseH);
      dragging.lastMatchedX = resized.matchedX;
      dragging.lastMatchedY = resized.matchedY;
      // 整列補助線を更新する（overlay が購読して描画する）。
      useAlignmentGuideStore.getState().setGuides(resized.guides);
      const target = targetRef.current;
      if (target) {
        target.style.left = `${next.x}px`;
        target.style.top = `${next.y}px`;
        target.style.width = `${next.w}px`;
        target.style.height = `${next.h}px`;
      }
      updateSelectionBoxPreview(next);

      // コネクタ（接続矢印）のリアルタイム追従用に、確定前のライブ枠を一時 store へ反映する。
      // 回転済み図形でもアンカーが回転位置を保つよう、現在の回転角も載せる。
      useConnectorDragStore.getState().setFrames({
        [objectId]: {
          x: next.x,
          y: next.y,
          w: next.w,
          h: next.h,
          rotation: dragging.baseRotation,
        },
      });

      // 他参加者へリサイズ中ゴーストを中継する（一定間隔で間引く）。
      const now = Date.now();
      if (now - dragging.lastTransientAt >= TRANSIENT_THROTTLE_MS) {
        dragging.lastTransientAt = now;
        dispatcher.previewTransforms([
          { objectId, transform: { x: next.x, y: next.y, w: next.w, h: next.h } },
        ]);
      }
    };

    // 最終フレームの確定処理（pointerup と lostpointercapture で共有する）。
    // グリッド吸着 ON のとき、最終 x/y/w/h を CANVAS_SNAP_PX 単位で丸める。
    // ただしアスペクト比固定（画像 / 動画 / スタンプ、または Shift+四隅）は w/h を個別に丸めると
    // 縦横比が崩れるため、位置（x/y）のみグリッドへ丸め、サイズは比率を保ったプレビュー値を採用する。
    // また整列スナップが成立した軸は、整列値を保つためグリッド丸めをスキップする
    // （移動経路の「整列成立軸はグリッドより整列を優先」と同方針）。
    const commitFrame = (
      next: { x: number; y: number; w: number; h: number },
      keepAspectFinal: boolean,
      matchedX: boolean,
      matchedY: boolean,
      baseH: number
    ): void => {
      const { gridSnapEnabled } = useCanvasStore.getState();
      // 整列成立軸は整列値をそのまま採用し、非成立軸のみグリッドへ丸める。
      const snapX = (value: number): number => (matchedX ? value : snapToGrid(value));
      const snapY = (value: number): number => (matchedY ? value : snapToGrid(value));
      const finalize = gridSnapEnabled
        ? keepAspectFinal
          ? // 位置のみグリッドへ丸め、サイズは比率を保ったプレビュー値を採用。
            // 境界到達時も比率を維持するため、個別丸めの clampFrameToCanvas ではなく専用 clamp を使う。
            clampAspectFrameToCanvas({
              x: snapX(next.x),
              y: snapY(next.y),
              w: next.w,
              h: next.h,
            })
          : clampFrameToCanvas({
              x: snapX(next.x),
              y: snapY(next.y),
              w: matchedX ? next.w : Math.max(minW, snapToGrid(next.w)),
              h: matchedY ? next.h : Math.max(minH, snapToGrid(next.h)),
            })
        : next;
      if (gridSnapEnabled) {
        const target = targetRef.current;
        if (target) {
          target.style.left = `${finalize.x}px`;
          target.style.top = `${finalize.y}px`;
          target.style.width = `${finalize.w}px`;
          target.style.height = `${finalize.h}px`;
        }
        updateSelectionBoxPreview(finalize);
      }
      const current = useCanvasDocStore.getState().objects[objectId];
      // 判定にはドラッグ開始時の高さを使う。現在の store 値を基準にすると、ドラッグ中に届いた
      // 他参加者の更新によって、横方向だけのリサイズを高さの手動指定と誤判定し得る。
      // またグリッド吸着後の値だけを見ると、格子外の高さが幅だけの操作でも丸められ、
      // 利用者が高さを動かしていないのに手動指定と誤判定するため、吸着前後の両方を確認する。
      const marksManualHeight =
        current?.type === "text" && next.h !== baseH && finalize.h !== baseH;
      dispatcher.transformObject(objectId, {
        ...finalize,
        ...(marksManualHeight ? { manualHeight: true } : {}),
      });
      // 確定（doc store へ反映）後にライブ枠・整列補助線を破棄し、コネクタを確定値での描画へ収束させる。
      useConnectorDragStore.getState().clearFrames([objectId]);
      useAlignmentGuideStore.getState().clearGuides();
    };

    const onPointerUp = (event: PointerEvent): void => {
      const dragging = draggingRef.current;
      if (!dragging || dragging.pointerId !== event.pointerId) return;
      // 最後の pointermove プレビュー枠をそのまま確定に使う。プレビューが一度も走っていない
      // （pointerdown 直後に pointerup）場合のみ、現在のキー状態で計算した枠へフォールバックする。
      let next: { x: number; y: number; w: number; h: number };
      let matchedX: boolean;
      let matchedY: boolean;
      if (dragging.lastPreviewFrame) {
        next = dragging.lastPreviewFrame;
        matchedX = dragging.lastMatchedX;
        matchedY = dragging.lastMatchedY;
      } else {
        const resized = computeResized(dragging, event.clientX, event.clientY, event.shiftKey);
        next = resized.frame;
        matchedX = resized.matchedX;
        matchedY = resized.matchedY;
      }
      // アスペクト比固定（lockAspect または Shift+四隅）が成立していたかを確定前に控える。
      const keepAspectFinal = dragging.lastKeepAspect;
      const baseH = dragging.baseH;
      draggingRef.current = null;
      if (handle.hasPointerCapture?.(event.pointerId)) {
        handle.releasePointerCapture?.(event.pointerId);
      }
      commitFrame(next, keepAspectFinal, matchedX, matchedY, baseH);
    };

    // pointercancel（ブラウザ / OS による中断、2 本指ピンチ開始時の取り消し）では commitFrame を
    // 呼ばず、直書きしたプレビュー枠を捨てて状態を初期化する。pointercancel は利用者がボタンや指を
    // 離した合図ではないため、確定すると意図しない大きさでリサイズが確定してしまう。
    // 状態を先に捨ててから pointer capture を解放し、解放に伴う lostpointercapture が届いても
    // 確定へ進まないようにする。
    const onPointerCancel = (event: PointerEvent): void => {
      const dragging = draggingRef.current;
      if (!dragging || dragging.pointerId !== event.pointerId) return;
      draggingRef.current = null;
      if (handle.hasPointerCapture?.(event.pointerId)) {
        handle.releasePointerCapture?.(event.pointerId);
      }
      revertResizePreview(targetRef.current, objectId);
      revertSelectionBoxFrame(targetRef.current, objectId);
      useConnectorDragStore.getState().clearFrames([objectId]);
      useAlignmentGuideStore.getState().clearGuides();
    };

    // pointerup を経ずにポインタキャプチャを失った場合（タブ切替・OS/ブラウザ都合のジェスチャ中断・
    // 要素差し替え等）の受け皿。最後のプレビュー枠を確定し、「直接 DOM プレビューだけが残って
    // 確定 op が送られない（見た目とストア・つまみが乖離したまま）」状態を残さない。
    // 通常フローでは pointerup が先に走って draggingRef が null になっているため二重確定しない。
    const onLostPointerCapture = (event: PointerEvent): void => {
      const dragging = draggingRef.current;
      if (!dragging || dragging.pointerId !== event.pointerId) return;
      const next = dragging.lastPreviewFrame;
      const keepAspectFinal = dragging.lastKeepAspect;
      const matchedX = dragging.lastMatchedX;
      const matchedY = dragging.lastMatchedY;
      const baseH = dragging.baseH;
      draggingRef.current = null;
      if (next) {
        commitFrame(next, keepAspectFinal, matchedX, matchedY, baseH);
      } else {
        // 一度もプレビューしていない（何も変えていない）ので確定せず破棄だけする。
        useConnectorDragStore.getState().clearFrames([objectId]);
        useAlignmentGuideStore.getState().clearGuides();
      }
    };

    handle.addEventListener("pointerdown", onPointerDown);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerCancel);
    handle.addEventListener("lostpointercapture", onLostPointerCapture);
    return () => {
      handle.removeEventListener("pointerdown", onPointerDown);
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerUp);
      handle.removeEventListener("pointercancel", onPointerCancel);
      handle.removeEventListener("lostpointercapture", onLostPointerCapture);
    };
  }, [
    direction,
    handleRef,
    objectId,
    dispatcher,
    initialX,
    initialY,
    initialW,
    initialH,
    initialRotation,
    lockAspect,
    minW,
    minH,
    readOnly,
    targetRef,
  ]);
}

// 中断したリサイズの直書きプレビュー（left/top/width/height）をストア値へ巻き戻す。
// テキストは minHeight 運用（React は height を設定しない）のため inline height は除去する。
function revertResizePreview(target: HTMLElement | null, objectId: string): void {
  if (!target) return;
  const obj = useCanvasDocStore.getState().objects[objectId];
  if (!obj) return;
  target.style.left = `${obj.x}px`;
  target.style.top = `${obj.y}px`;
  target.style.width = `${obj.w}px`;
  if (obj.type === "text") {
    target.style.height = "";
  } else {
    target.style.height = `${obj.h}px`;
  }
}

// 中断したリサイズで選択ボックス（枠とつまみ）へ直書きしたプレビューをストア値へ巻き戻す。
// React は仮想スタイルが不変な限り書き戻さないため、ここで戻さないと本体だけ元の大きさへ復帰し、
// 枠とつまみだけが中断時点の大きさで取り残される。
// テキストは本文量により実描画高さが保存値 h（minHeight 運用）を超え得るため、巻き戻し後の実測値を
// 優先する（選択オーバーレイが measuredHeight を使って枠を描くのと同じ基準）。実測できない環境では
// 保存値へ戻す。
function revertSelectionBoxFrame(target: HTMLElement | null, objectId: string): void {
  const obj = useCanvasDocStore.getState().objects[objectId];
  if (!obj) return;
  const measured = obj.type === "text" ? (target?.offsetHeight ?? 0) : 0;
  updateSelectionBoxPreview({ x: obj.x, y: obj.y, w: obj.w, h: measured > 0 ? measured : obj.h });
}

function updateSelectionBoxPreview(next: {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}): void {
  const box = document.querySelector<HTMLElement>('[data-testid="canvas-selection-box"]');
  if (!box) return;
  box.style.left = `${next.x}px`;
  box.style.top = `${next.y}px`;
  box.style.width = `${next.w}px`;
  box.style.height = `${next.h}px`;
}

function snapToGrid(value: number): number {
  return Math.round(value / CANVAS_SNAP_PX) * CANVAS_SNAP_PX;
}
